<template>
  <div class="flex flex-col h-full w-full">
    <div class="flex flex-col h-full bg-white p-5 rounded-xl overflow-auto">
      <!-- Back link -->
      <button
        type="button"
        class="self-start inline-flex items-center gap-1 text-sm text-gray-600 hover:text-primary-900 cursor-pointer mb-3"
        @click="goBack"
      >
        <v-icon size="18">mdi-arrow-left</v-icon>
        {{ $t("reports.ai_chat_transcript.back") }}
      </button>

      <!-- Header -->
      <div class="flex flex-col gap-1 min-w-0 mb-4">
        <span class="text-xs font-mono text-gray-500 tracking-wide">
          {{ $t("reports.ai_chat_transcript.head_conversation_id") }}: {{ conversationId }}
        </span>
        <h2 class="text-xl font-semibold text-gray-800">
          {{ $t("reports.ai_chat_transcript.details_title") }}
        </h2>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex flex-1 justify-center items-center py-16">
        <v-progress-circular indeterminate color="primary" />
        <span class="ml-3 text-gray-500">{{ $t("reports.ai_chat_transcript.loading") }}</span>
      </div>

      <!-- Error / not found -->
      <div
        v-else-if="error || !conversation"
        class="flex flex-col flex-1 justify-center items-center py-16 text-center"
      >
        <v-icon :color="error ? 'error' : 'grey'" size="48">
          {{ error ? "mdi-alert-circle-outline" : "mdi-text-box-remove-outline" }}
        </v-icon>
        <p class="mt-3 text-gray-500">{{ error || $t("reports.ai_chat_transcript.no_data") }}</p>
      </div>

      <!-- Content -->
      <div v-else class="flex flex-col gap-5">
        <!-- Metadata -->
        <div class="bg-white rounded-xl border border-gray-200 p-4">
          <div class="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div v-for="meta in conversationMeta" :key="meta.label" class="min-w-0">
              <div class="text-[11px] text-gray-400 uppercase tracking-wide">{{ meta.label }}</div>
              <div class="text-gray-800 break-all" dir="ltr">{{ meta.value }}</div>
            </div>
          </div>
        </div>

        <!-- Summary -->
        <div v-if="conversation.summary" class="bg-stone-50 rounded-xl border border-stone-200 p-4">
          <h3 class="font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <v-icon size="18" color="primary">mdi-text-box-outline</v-icon>
            {{ $t("reports.ai_chat_transcript.summary_title") }}
          </h3>
          <p class="text-sm text-gray-800 whitespace-pre-wrap" :dir="containsArabic(conversation.summary) ? 'rtl' : 'ltr'">
            {{ conversation.summary }}
          </p>
        </div>

        <!-- Tags (shown when present; populated in the future) -->
        <div v-if="tags.length" class="bg-white rounded-xl border border-gray-200 p-4">
          <h3 class="font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <v-icon size="18" color="primary">mdi-tag-multiple-outline</v-icon>
            {{ $t("reports.ai_chat_transcript.tags_title") }}
          </h3>
          <div class="flex flex-wrap gap-2">
            <span
              v-for="(tag, idx) in tags"
              :key="idx"
              class="inline-block px-2 py-0.5 rounded-full text-xs bg-primary-50 text-primary-900 border border-primary-100"
            >
              {{ tag }}
            </span>
          </div>
        </div>

        <!-- Transcript -->
        <div class="bg-white rounded-xl border border-gray-200 p-4">
          <h3 class="font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <v-icon size="18" color="primary">mdi-forum-outline</v-icon>
            {{ $t("reports.ai_chat_transcript.transcript_title") }}
          </h3>
          <div v-if="transcriptLines.length === 0" class="text-center py-6 text-gray-400 text-sm">
            {{ $t("reports.ai_chat_transcript.no_transcript") }}
          </div>
          <div v-else class="space-y-2">
            <div
              v-for="(line, idx) in transcriptLines"
              :key="idx"
              class="flex"
              :class="line.party === 'customer' ? 'justify-start' : 'justify-end'"
            >
              <div
                class="max-w-[80%] rounded-lg px-3 py-2 text-sm border whitespace-pre-wrap break-words"
                :class="bubbleClass(line.party)"
                :dir="containsArabic(line.text) ? 'rtl' : 'ltr'"
              >
                <div class="flex items-center gap-2 mb-0.5">
                  <span class="text-[11px] font-semibold" :class="labelClass(line.party)">
                    {{ line.label }}
                  </span>
                  <span v-if="line.time" class="text-[10px] text-gray-400">{{ line.time }}</span>
                </div>
                {{ line.text }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import moment from "moment";

export default {
  name: "ai-chat-details",
  data() {
    return {
      loading: false,
      error: null,
      conversation: null,
    };
  },
  computed: {
    conversationId() {
      return this.$route.query.conversation_id || "";
    },
    conversationMeta() {
      const c = this.conversation || {};
      const t = (k) => this.$t("reports.ai_chat_transcript." + k);
      return [
        { label: t("meta_customer"), value: c.customer || "-" },
        { label: t("meta_channel"), value: this.channelLabel(c.channel) },
        { label: t("meta_agent"), value: c.agent || "-" },
        { label: t("meta_outcome"), value: c.outcome ? this.outcomeLabel(c.outcome) : "-" },
        { label: t("meta_started"), value: c.started_at || "-" },
        { label: t("meta_ended"), value: c.closed_at || c.ended_at || "-" },
      ];
    },
    tags() {
      const t = this.conversation?.tags;
      return Array.isArray(t) ? t.filter(Boolean) : [];
    },
    transcriptLines() {
      const c = this.conversation;
      const msgs = c?.transcript;
      if (!Array.isArray(msgs)) return [];
      const customer = String(c.customer || "");
      return msgs
        .map((m) => {
          const from = String(m.from || "");
          let party;
          if (customer && from === customer) party = "customer";
          else if (from === "ai_bot" || from === "bot" || from === "assistant") party = "assistant";
          else party = "agent";
          return {
            party,
            label: this.partyLabel(party, from),
            text: m.message || m.content || m.text || "",
            time: this.formatTime(m.time),
          };
        })
        .filter((m) => m.text);
    },
  },
  methods: {
    ...mapActions(["GetAIChatDetails", "SetMessageDialog"]),

    goBack() {
      this.$router.back();
    },
    containsArabic(text) {
      return /[؀-ۿ]/.test(String(text || ""));
    },
    channelLabel(type) {
      const key = "reports.ai_chat_transcript.channels." + Number(type);
      if (this.$te(key)) return this.$t(key);
      return this.$t("reports.ai_chat_transcript.channels.default") + " " + (type ?? "");
    },
    outcomeLabel(outcome) {
      const key = "reports.ai_chat_transcript.outcomes." + String(outcome).toLowerCase();
      return this.$te(key) ? this.$t(key) : outcome;
    },
    partyLabel(party, from) {
      if (party === "customer") return this.$t("reports.ai_chat_transcript.role_customer");
      if (party === "assistant") return this.$t("reports.ai_chat_transcript.role_assistant");
      const base = this.$t("reports.ai_chat_transcript.role_agent");
      return from && from !== "agent" ? `${base} ${from}` : base;
    },
    bubbleClass(party) {
      if (party === "customer") return "border-green-100 bg-green-50/60";
      if (party === "assistant") return "border-blue-100 bg-blue-50/60";
      return "border-purple-100 bg-purple-50/60";
    },
    labelClass(party) {
      if (party === "customer") return "text-green-700";
      if (party === "assistant") return "text-blue-700";
      return "text-purple-700";
    },
    formatTime(time) {
      if (time == null || time === "") return "";
      const str = String(time);
      if (/^\d{9,11}$/.test(str)) {
        return moment.unix(Number(str)).format("YYYY-MM-DD HH:mm");
      }
      const m = moment(str);
      return m.isValid() ? m.format("YYYY-MM-DD HH:mm") : str;
    },

    async fetchDetails() {
      if (!this.conversationId) {
        this.error = this.$t("reports.ai_chat_transcript.no_data");
        return;
      }
      this.loading = true;
      this.error = null;
      try {
        const resp = await this.GetAIChatDetails({ conversation_id: this.conversationId });
        if (resp && resp.success) {
          const result = resp.result || {};
          this.conversation = result.conversation || result.data || result || null;
        } else {
          this.error = resp?.message || this.$t("reports.ai_chat_transcript.no_data");
        }
      } catch (err) {
        this.error = err.response?.data?.message || err.message || "Failed to load conversation";
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.fetchDetails();
  },
};
</script>

<style scoped></style>
